import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { X, FileText, Save, Download, ExternalLink, RefreshCw, Shield } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * InvoiceDataModal — admin sets per-appointment invoice state.
 *
 * Props:
 *   isOpen      : boolean
 *   onClose     : () => void
 *   appointment : { id, ... } - appointment row from the list
 *   onSaved?    : () => void  - optional callback after a successful save
 *
 * Surfaces:
 *   - Template chooser (lists all invoice_templates)
 *   - Diagnostic code free-text input (only shown if the chosen template
 *     has show_diagnostic_code enabled — but always editable as a fallback)
 *   - Per-question response inputs for each question on the chosen template
 *     where ask_at !== 'invoice_only' (those are blank lines on the printed
 *     PDF; they don't need pre-filled answers)
 *   - "Download invoice (PDF)" — opens the rendered PDF in a new tab using
 *     a fetch + Blob URL so the Bearer token is included
 *   - "Open HTML preview" — same, but the HTML version (faster, no PDF
 *     dependency)
 *
 * Behavior:
 *   - On open, fetches /appointments/{id}/invoice/data which returns the
 *     current saved state plus the chosen template's questions inline
 *   - When the admin switches template via the dropdown, we re-fetch any
 *     question definitions for the new template so the form re-renders
 *   - Save calls PUT /appointments/{id}/invoice/data with the full payload
 */
const InvoiceDataModal = ({ isOpen, onClose, appointment, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [responses, setResponses] = useState({});
  const [diagnosticCode, setDiagnosticCode] = useState('');
  const [showDiagnosticField, setShowDiagnosticField] = useState(false);
  const [pdfGeneratedAt, setPdfGeneratedAt] = useState(null);

  // Insurance-ready fields (migration 036). Always editable in the modal
  // even when the chosen template doesn't currently have
  // `show_insurance_block` enabled — admin can pre-fill claim info before
  // toggling the template flag, and we don't want a hidden form section
  // to swallow already-saved values.
  const [insurerName, setInsurerName] = useState('');
  const [policyNumber, setPolicyNumber] = useState('');
  const [groupNumber, setGroupNumber] = useState('');
  const [claimReference, setClaimReference] = useState('');
  const [paidDate, setPaidDate] = useState('');
  const [isReceipt, setIsReceipt] = useState(false);
  const [insuranceNotes, setInsuranceNotes] = useState('');
  const [insuranceOpen, setInsuranceOpen] = useState(false);

  const apptId = appointment?.id;

  const authHeader = () => ({
    headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` },
  });

  const loadAll = useCallback(async () => {
    if (!apptId) return;
    setLoading(true);
    try {
      // List of all templates (for the dropdown) and the per-appointment data.
      const [tplsRes, dataRes] = await Promise.all([
        axios.get(`${API_URL}/invoice-templates`, authHeader()),
        axios.get(`${API_URL}/appointments/${apptId}/invoice/data`, authHeader()),
      ]);
      setTemplates(tplsRes.data || []);
      const d = dataRes.data || {};
      setSelectedTemplateId(d.invoice_template_id || null);
      setDiagnosticCode(d.diagnostic_code || '');
      setQuestions(d.questions || []);
      setResponses(d.responses || {});
      setPdfGeneratedAt(d.pdf_generated_at || null);
      setInsurerName(d.insurer_name || '');
      setPolicyNumber(d.policy_number || '');
      setGroupNumber(d.group_number || '');
      setClaimReference(d.claim_reference || '');
      setPaidDate(d.paid_date || '');
      setIsReceipt(!!d.is_receipt);
      setInsuranceNotes(d.insurance_notes || '');
      // Auto-expand the section if any insurance value is already set, so
      // a returning admin sees what's saved without having to click open.
      const anyInsuranceSet = !!(d.insurer_name || d.policy_number || d.group_number
        || d.claim_reference || d.paid_date || d.is_receipt || d.insurance_notes);
      setInsuranceOpen(anyInsuranceSet);
      const settings = d.template?.settings || {};
      setShowDiagnosticField(!!settings.show_diagnostic_code);
    } catch (e) {
      toast.error('Failed to load invoice data');
    } finally {
      setLoading(false);
    }
  }, [apptId]);

  useEffect(() => {
    if (isOpen) loadAll();
  }, [isOpen, loadAll]);

  // When the admin switches template, refetch its questions + settings.
  const handleTemplateChange = async (newId) => {
    setSelectedTemplateId(newId);
    if (!newId) {
      setQuestions([]);
      setShowDiagnosticField(false);
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/invoice-templates/${newId}`, authHeader());
      const tpl = res.data || {};
      setQuestions(tpl.questions || []);
      const settings = tpl.settings || {};
      setShowDiagnosticField(!!settings.show_diagnostic_code);
    } catch (e) {
      toast.error('Failed to load template details');
    }
  };

  const updateResponse = (qid, value) => {
    setResponses((prev) => ({ ...prev, [qid]: value }));
  };

  const handleSave = async () => {
    if (!apptId) return;
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/appointments/${apptId}/invoice/data`,
        {
          invoice_template_id: selectedTemplateId,
          diagnostic_code: diagnosticCode || null,
          responses,
          insurer_name: insurerName || null,
          policy_number: policyNumber || null,
          group_number: groupNumber || null,
          claim_reference: claimReference || null,
          paid_date: paidDate || null,
          is_receipt: isReceipt,
          insurance_notes: insuranceNotes || null,
        },
        authHeader()
      );
      toast.success('Invoice data saved');
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Open the rendered PDF (or HTML) in a new tab. Uses fetch + Blob so the
   * Bearer token can be sent (a plain anchor href cannot include headers).
   */
  const openRendered = async (kind) => {
    try {
      const url = `${API_URL}/appointments/${apptId}/invoice.${kind}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` },
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || res.statusText);
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      if (kind === 'pdf') setPdfGeneratedAt(new Date().toISOString());
    } catch (e) {
      toast.error('Failed to render: ' + (e.message || 'unknown error'));
    }
  };

  if (!isOpen || !appointment) return null;

  // Filter to questions that the admin should be able to pre-fill.
  // 'invoice_only' questions become blank fillable lines on the PDF, so they
  // are intentionally NOT pre-filled here.
  const editableQuestions = questions.filter((q) => q.ask_at !== 'invoice_only' && q.is_active !== false);
  const invoiceOnlyCount = questions.filter((q) => q.ask_at === 'invoice_only' && q.is_active !== false).length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <FileText size={22} className="text-primary-600" />
            <h2 className="text-xl font-bold text-gray-900">Invoice data</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded"
            title="Close"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 overflow-y-auto flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Appointment summary */}
              <div className="bg-gray-50 border border-gray-200 rounded p-3 text-sm">
                <div className="text-gray-500 text-xs uppercase tracking-wide">Appointment</div>
                <div className="font-semibold text-gray-800 mt-0.5">
                  {appointment.client_first_name} {appointment.client_last_name}
                </div>
                <div className="text-gray-600 text-xs">
                  {appointment.appointment_date} {appointment.appointment_time}
                  {appointment.service_name ? ` · ${appointment.service_name}` : ''}
                </div>
                {pdfGeneratedAt && (
                  <div className="text-gray-500 text-xs mt-1">
                    PDF last generated: {String(pdfGeneratedAt).slice(0, 19).replace('T', ' ')}
                  </div>
                )}
              </div>

              {/* Template selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Invoice template
                </label>
                <select
                  className="input w-full"
                  value={selectedTemplateId || ''}
                  onChange={(e) => handleTemplateChange(e.target.value || null)}
                >
                  <option value="">Use system default</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}{t.is_default ? ' (default)' : ''}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Override the default template just for this appointment. "Use
                  system default" means whichever template is currently marked
                  default will be used at PDF time.
                </p>
              </div>

              {/* Diagnostic code (only if the template enables it) */}
              {showDiagnosticField && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Diagnostic code
                  </label>
                  <input
                    type="text"
                    className="input w-full"
                    value={diagnosticCode}
                    onChange={(e) => setDiagnosticCode(e.target.value)}
                    maxLength={80}
                    placeholder="e.g. M54.5"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Free-form. ICD-10, CPT, or any other code your insurer
                    expects. Appears below the provider info block on the
                    invoice.
                  </p>
                </div>
              )}

              {/* Per-question responses */}
              {editableQuestions.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-2">
                    Custom question answers
                  </h3>
                  <div className="space-y-3">
                    {editableQuestions.map((q) => (
                      <QuestionInput
                        key={q.id}
                        question={q}
                        value={responses[q.id] ?? ''}
                        onChange={(v) => updateResponse(q.id, v)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Insurance / claim section. Collapsible to keep the modal
                  short for businesses that don't use it. */}
              <div className="border border-gray-200 rounded">
                <button
                  type="button"
                  onClick={() => setInsuranceOpen((v) => !v)}
                  className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 text-left"
                >
                  <div className="flex items-center space-x-2">
                    <Shield size={16} className="text-primary-600" />
                    <span className="text-sm font-semibold text-gray-800">Insurance / claim</span>
                  </div>
                  <span className="text-xs text-gray-500">{insuranceOpen ? 'Hide' : 'Show'}</span>
                </button>
                {insuranceOpen && (
                  <div className="p-3 space-y-3">
                    <p className="text-xs text-gray-500">
                      For clients submitting receipts to their own insurer.
                      Fields render on the invoice only when the template's
                      "Show insurance block" toggle is on.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Insurer</label>
                        <input
                          type="text"
                          className="input w-full"
                          value={insurerName}
                          onChange={(e) => setInsurerName(e.target.value)}
                          maxLength={120}
                          placeholder="e.g. Sun Life"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Policy / member number</label>
                        <input
                          type="text"
                          className="input w-full"
                          value={policyNumber}
                          onChange={(e) => setPolicyNumber(e.target.value)}
                          maxLength={80}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Group / plan number</label>
                        <input
                          type="text"
                          className="input w-full"
                          value={groupNumber}
                          onChange={(e) => setGroupNumber(e.target.value)}
                          maxLength={80}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Claim / pre-auth reference</label>
                        <input
                          type="text"
                          className="input w-full"
                          value={claimReference}
                          onChange={(e) => setClaimReference(e.target.value)}
                          maxLength={120}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Paid date</label>
                        <input
                          type="date"
                          className="input w-full"
                          value={paidDate}
                          onChange={(e) => setPaidDate(e.target.value)}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Renders only when the template's "Show paid date" toggle is on.
                        </p>
                      </div>
                      <div className="flex items-end">
                        <label className="flex items-center space-x-2 cursor-pointer pb-2">
                          <input
                            type="checkbox"
                            checked={isReceipt}
                            onChange={(e) => setIsReceipt(e.target.checked)}
                            className="h-4 w-4 text-primary-600 rounded"
                          />
                          <span className="text-sm text-gray-700">Render as <strong>Receipt</strong> (paid)</span>
                        </label>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Insurance notes</label>
                      <textarea
                        className="input w-full"
                        rows={2}
                        value={insuranceNotes}
                        onChange={(e) => setInsuranceNotes(e.target.value)}
                        placeholder="e.g. Direct billing not available — please reimburse policyholder."
                      />
                    </div>
                  </div>
                )}
              </div>

              {invoiceOnlyCount > 0 && (
                <div className="text-xs text-gray-500 italic">
                  Note: {invoiceOnlyCount} question{invoiceOnlyCount === 1 ? ' is' : 's are'} set to "fillable on the invoice". {invoiceOnlyCount === 1 ? 'It' : 'They'} will appear as blank line{invoiceOnlyCount === 1 ? '' : 's'} on the printed PDF for the client to fill in.
                </div>
              )}

              {/* Render actions */}
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm font-semibold text-gray-800 mb-2">
                  Render & download
                </h3>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => openRendered('pdf')}
                    className="inline-flex items-center space-x-2 px-3 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 text-sm"
                  >
                    <Download size={16} />
                    <span>Generate &amp; open PDF</span>
                  </button>
                  <button
                    onClick={() => openRendered('html')}
                    className="inline-flex items-center space-x-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded text-sm"
                  >
                    <ExternalLink size={16} />
                    <span>Quick HTML preview</span>
                  </button>
                  <button
                    onClick={loadAll}
                    className="inline-flex items-center space-x-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded text-sm"
                    title="Reload current saved values"
                  >
                    <RefreshCw size={16} />
                    <span>Reload</span>
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Tip: save your changes before generating so the PDF reflects
                  the latest template choice and answers.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-end space-x-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded transition-colors"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
          >
            <Save size={16} />
            <span>{saving ? 'Saving…' : 'Save'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Renders the right input control for a question's field_type.
 */
const QuestionInput = ({ question, value, onChange }) => {
  const label = (
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {question.label}
      {question.required && <span className="text-red-500 ml-1">*</span>}
      <span className="text-xs text-gray-400 ml-2 font-normal">
        (asked at {question.ask_at === 'booking' ? 'booking' : question.ask_at === 'payment' ? 'payment' : 'invoice'})
      </span>
    </label>
  );

  switch (question.field_type) {
    case 'textarea':
      return (
        <div>
          {label}
          <textarea
            className="input w-full"
            rows={3}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      );
    case 'select':
      return (
        <div>
          {label}
          <select
            className="input w-full"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
          >
            <option value="">— Select —</option>
            {(question.options || []).map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      );
    case 'checkbox':
      return (
        <div>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={value === 'Yes' || value === true || value === '1'}
              onChange={(e) => onChange(e.target.checked ? 'Yes' : 'No')}
              className="h-4 w-4 text-primary-600 rounded"
            />
            <span className="text-sm font-medium text-gray-700">
              {question.label}
              {question.required && <span className="text-red-500 ml-1">*</span>}
            </span>
          </label>
        </div>
      );
    case 'text':
    default:
      return (
        <div>
          {label}
          <input
            type="text"
            className="input w-full"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            maxLength={500}
          />
        </div>
      );
  }
};

export default InvoiceDataModal;
